import React, { useState, useCallback } from 'react';
import { ColumnNavigation } from './common/ColumnNavigation';
import {
  Box as MuiBox,
  Typography as MuiTypography,
  Card as MuiCard,
  CardContent as MuiCardContent,
  CardHeader as MuiCardHeader,
  useMediaQuery,
  useTheme,
  Fade as MuiFade,
  Button as MuiButton,
  TextField as MuiTextField,
  Grid as MuiGrid,
  Divider as MuiDivider,
  Chip as MuiChip,
  Alert as MuiAlert,
  List as MuiList,
  ListItem as MuiListItem,
  ListItemText as MuiListItemText,
  ListItemIcon as MuiListItemIcon,
  Paper as MuiPaper,
  Table as MuiTable,
  TableBody as MuiTableBody,
  TableCell as MuiTableCell,
  TableContainer as MuiTableContainer,
  TableHead as MuiTableHead,
  TableRow as MuiTableRow,
  LinearProgress as MuiLinearProgress,
  Dialog as MuiDialog,
  DialogTitle as MuiDialogTitle,
  DialogContent as MuiDialogContent,
  DialogActions as MuiDialogActions,
  Select as MuiSelect,
  MenuItem as MuiMenuItem,
  FormControl as MuiFormControl,
  InputLabel as MuiInputLabel,
  Tooltip as MuiTooltip,
  IconButton as MuiIconButton,
  Snackbar as MuiSnackbar
} from '@mui/material';
import {
  AdminPanelSettings as AdminIcon,
  Security as SecurityIcon,
  MenuBook as TerminologyIcon,
  Dashboard as DashboardIcon,
  Visibility as VisibilityIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  CloudDone as CloudDoneIcon,
  Storage as StorageIcon,
  Assessment as AssessmentIcon,
  Help as HelpIcon,
  Translate as TranslateIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon,
  ContentCopy as ContentCopyIcon,
  SmartToy as SmartToyIcon,
  Person as PersonIcon
} from '@mui/icons-material';

// Import the existing DataGovernanceDashboard content and TerminologyGuidanceModal content
// We'll inline the relevant components for the Admin panel

interface AdminProps {
  currentView: string;
  onViewChange: (view: string) => void;
  onAIChatToggle: () => void;
}

interface ComplianceViolation {
  id: string;
  type: string;
  severity: 'Low' | 'Medium' | 'High';
  entity: string;
  date: string;
  status: 'Active' | 'Resolved';
  description: string;
  aiPrompt: string;
}

const Admin: React.FC<AdminProps> = ({ currentView, onViewChange, onAIChatToggle }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [selectedSection, setSelectedSection] = useState('data-governance');
  const [copySnackbar, setCopySnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  
  // Data Governance state
  const [governanceData, setGovernanceData] = useState({
    complianceScore: 87,
    dataQualityScore: 92,
    auditStatus: 'passing',
    lastAudit: '2024-01-20',
    activeViolations: 3,
    resolvedViolations: 15,
    dataAssets: 142,
    governedFields: 89
  });

  // Enhanced violations data with AI prompts
  const [violations, setViolations] = useState<ComplianceViolation[]>([
    {
      id: 'v1',
      type: 'Duplicate Naming',
      severity: 'Medium',
      entity: 'Feature: "User Dashboard"',
      date: '2024-01-19',
      status: 'Resolved',
      description: 'Multiple features with identical names detected',
      aiPrompt: `**Compliance Issue Resolution Request**

**Problem:** Duplicate Naming Violation
**Entity:** Feature: "User Dashboard"
**Severity:** Medium
**Current Platform State:** 87% compliance (target: 100%)

**Context:**
I'm managing an Intelligenic SDLC platform where multiple features have identical names, creating confusion and governance violations.

**Specific Request:**
Please provide a comprehensive strategy to:
1. Implement automated duplicate detection rules
2. Create a naming convention system that prevents duplicates
3. Design a renaming workflow that maintains entity relationships
4. Establish validation rules for new feature creation
5. Generate suggestions for disambiguating similar feature names

**Expected Outcome:**
- Zero duplicate names across all entities
- Automated prevention system
- Clear naming guidelines
- 100% compliance score achievement

**Technical Environment:** React/MUI frontend, nomenclature validation system

Please provide actionable steps with code examples where applicable.`
    },
    {
      id: 'v2',
      type: 'Reserved Word Usage',
      severity: 'High',
      entity: 'Project: "System"',
      date: '2024-01-18',
      status: 'Active',
      description: 'Using system reserved words in entity names',
      aiPrompt: `**Critical Compliance Resolution Required**

**Problem:** Reserved Word Usage Violation
**Entity:** Project: "System"
**Severity:** High (Blocking 100% compliance)
**Current Platform State:** 87% compliance (target: 100%)

**Context:**
My Intelligenic SDLC platform has a project named "System" which conflicts with reserved system terminology, creating high-severity governance violations.

**Immediate Action Required:**
1. Provide a list of ALL reserved words that should be blocked
2. Create validation rules to prevent reserved word usage
3. Design an entity renaming system that preserves all relationships
4. Implement real-time validation feedback for users
5. Suggest alternative naming patterns for system-related projects

**Constraints:**
- Cannot break existing project relationships
- Must maintain data integrity during renaming
- Need user-friendly error messages
- Requires seamless migration path

**Success Criteria:**
- No reserved words in any entity names
- Proactive prevention system
- 100% compliance achievement
- Zero disruption to existing workflows

Please provide step-by-step implementation guide with validation logic and user experience considerations.`
    },
    {
      id: 'v3',
      type: 'Invalid Characters',
      severity: 'Low',
      entity: 'Feature: "API-2.0"',
      date: '2024-01-17',
      status: 'Resolved',
      description: 'Special characters in entity names violate naming standards',
      aiPrompt: `**Data Quality Enhancement Request**

**Problem:** Invalid Characters in Entity Names
**Entity:** Feature: "API-2.0"
**Severity:** Low (Optimization opportunity)
**Current Platform State:** 87% compliance (target: 100%)

**Context:**
My Intelligenic platform allows special characters in feature names that don't comply with our naming standards, preventing 100% compliance achievement.

**Optimization Goals:**
1. Define comprehensive character validation rules
2. Create user-friendly naming guidelines
3. Implement auto-suggestion for name corrections
4. Build batch correction tools for existing entities
5. Design progressive enhancement for name validation

**Character Policy Needed:**
- Allowed: Letters, numbers, spaces, hyphens
- Forbidden: Special symbols, punctuation (except hyphens)
- Version numbering: Establish standard format
- Internationalization: Unicode support considerations

**Deliverables Required:**
- Character validation regex patterns
- User input sanitization logic
- Migration script for existing names
- Auto-suggestion algorithm
- User guidance tooltips

**Success Metrics:**
- 100% character compliance
- Improved name consistency
- Enhanced user experience
- Zero naming-related errors

Please provide technical implementation with user experience best practices.`
    }
  ]);

  // Terminology state
  const [terminologySettings, setTerminologySettings] = useState({
    methodology: 'scrum',
    customTerms: {
      'theme': 'Theme',
      'initiative': 'Initiative', 
      'feature': 'Feature',
      'epic': 'Epic',
      'story': 'Story',
      'sprint': 'Sprint',
      'backlog-item': 'Backlog Item',
      'acceptance-criteria': 'Acceptance Criteria',
      'roadmap': 'Roadmap'
    }
  });

  const [tempTerminologySettings, setTempTerminologySettings] = useState(terminologySettings);
  const [terminologyHasChanges, setTerminologyHasChanges] = useState(false);

  // Navigation items for the Admin panel - Updated after moving sections to Profile
  const navigationItems = [
    // Items moved to Profile Panel:
    // - Data Governance Dashboard → Profile > Privacy & Data Settings section
    // - Naming Guidelines & Governance → Profile > Privacy & Data Settings section
  ];

  // Methodology definitions
  const methodologies = [
    {
      id: 'scrum',
      name: 'Scrum',
      description: 'Standard Scrum terminology and practices',
      defaultTerms: {
        'theme': 'Theme',
        'initiative': 'Initiative',
        'feature': 'Feature', 
        'epic': 'Epic',
        'story': 'User Story',
        'sprint': 'Sprint',
        'backlog-item': 'Product Backlog Item',
        'acceptance-criteria': 'Acceptance Criteria',
        'roadmap': 'Product Roadmap'
      }
    },
    {
      id: 'jira',
      name: 'Jira/Atlassian',
      description: 'Jira-aligned terminology for seamless integration',
      defaultTerms: {
        'theme': 'Theme',
        'initiative': 'Initiative',
        'feature': 'Feature',
        'epic': 'Epic',
        'story': 'Story',
        'sprint': 'Sprint',
        'backlog-item': 'Issue',
        'acceptance-criteria': 'Acceptance Criteria',
        'roadmap': 'Roadmap'
      }
    },
    {
      id: 'safe',
      name: 'SAFe (Scaled Agile)',
      description: 'Scaled Agile Framework terminology',
      defaultTerms: {
        'theme': 'Strategic Theme',
        'initiative': 'Portfolio Epic',
        'feature': 'Capability',
        'epic': 'Solution Epic',
        'story': 'Feature',
        'sprint': 'Iteration',
        'backlog-item': 'Program Backlog Item',
        'acceptance-criteria': 'Acceptance Criteria',
        'roadmap': 'Solution Roadmap'
      }
    },
    {
      id: 'azure-devops',
      name: 'Azure DevOps',
      description: 'Microsoft Azure DevOps terminology',
      defaultTerms: {
        'theme': 'Theme',
        'initiative': 'Initiative',
        'feature': 'Feature',
        'epic': 'Epic',
        'story': 'User Story',
        'sprint': 'Sprint',
        'backlog-item': 'Work Item',
        'acceptance-criteria': 'Acceptance Criteria',
        'roadmap': 'Feature Timeline'
      }
    },
    {
      id: 'custom',
      name: 'Custom',
      description: 'Define your own terminology preferences',
      defaultTerms: {
        'theme': 'Theme',
        'initiative': 'Initiative',
        'feature': 'Feature',
        'epic': 'Epic', 
        'story': 'Story',
        'sprint': 'Sprint',
        'backlog-item': 'Backlog Item',
        'acceptance-criteria': 'Acceptance Criteria',
        'roadmap': 'Roadmap'
      }
    }
  ];

  // Term definitions for help tooltips
  const termDefinitions = {
    'theme': 'High-level strategic direction or business objective that groups related initiatives',
    'initiative': 'Large body of work that delivers significant business value and may span multiple teams',
    'feature': 'Specific functionality or capability that provides value to end users',
    'epic': 'Large user story that can be broken down into smaller stories for development',
    'story': 'Small, manageable piece of functionality from the user\'s perspective',
    'sprint': 'Time-boxed period during which development work is completed',
    'backlog-item': 'Item in the product backlog representing work to be done',
    'acceptance-criteria': 'Conditions that must be met for a story to be considered complete',
    'roadmap': 'High-level view of product direction and planned features over time'
  };

  const handleNavigationClick = (itemId: string) => {
    setSelectedSection(itemId);
  };

  const handleMethodologyChange = (event: { target: { value: string } }) => {
    const selectedMethodology = methodologies.find(m => m.id === event.target.value);
    if (selectedMethodology) {
      const newSettings = {
        methodology: selectedMethodology.id,
        customTerms: { ...selectedMethodology.defaultTerms }
      };
      setTempTerminologySettings(newSettings);
      setTerminologyHasChanges(
        JSON.stringify(newSettings) !== JSON.stringify(terminologySettings)
      );
    }
  };

  const handleTermChange = (termKey: string, newValue: string) => {
    const newSettings = {
      ...tempTerminologySettings,
      customTerms: {
        ...tempTerminologySettings.customTerms,
        [termKey]: newValue
      }
    };
    setTempTerminologySettings(newSettings);
    setTerminologyHasChanges(
      JSON.stringify(newSettings) !== JSON.stringify(terminologySettings)
    );
  };

  const handleSaveTerminology = () => {
    setTerminologySettings(tempTerminologySettings);
    setTerminologyHasChanges(false);
  };

  const handleRefreshGovernance = () => {
    // Simulate refresh of governance data
    setGovernanceData(prev => ({
      ...prev,
      complianceScore: Math.floor(Math.random() * 20) + 80,
      dataQualityScore: Math.floor(Math.random() * 20) + 80,
      lastAudit: new Date().toISOString().split('T')[0]
    }));
  };

  // Enhanced copy functionality with comprehensive fallback methods
  const handleCopyAIPrompt = async (violation: ComplianceViolation) => {
    try {
      // Method 1: Modern Clipboard API (most reliable when available)
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(violation.aiPrompt);
        setCopySnackbar({
          open: true,
          message: `AI prompt for "${violation.type}" copied to clipboard! Paste in Claude to get resolution guidance.`,
          severity: 'success'
        });
        return;
      }

      // Method 2: Fallback using document.execCommand (deprecated but widely supported)
      const success = await fallbackCopyTextToClipboard(violation.aiPrompt);
      if (success) {
        setCopySnackbar({
          open: true,
          message: `AI prompt for "${violation.type}" copied to clipboard! Paste in Claude to get resolution guidance.`,
          severity: 'success'
        });
      } else {
        throw new Error('All copy methods failed');
      }
    } catch (err) {
      console.error('Failed to copy prompt:', err);
      
      // Auto-select the text in the corresponding textarea as a final fallback
      try {
        const textareas = document.querySelectorAll('textarea');
        const targetTextarea = Array.from(textareas).find(textarea => 
          textarea.value === violation.aiPrompt
        );
        
        if (targetTextarea) {
          targetTextarea.focus();
          targetTextarea.select();
          targetTextarea.setSelectionRange(0, targetTextarea.value.length);
          
          setCopySnackbar({
            open: true,
            message: `Copy failed due to browser restrictions. Text has been selected in the AI Prompt column - use Ctrl+C (Cmd+C on Mac) to copy.`,
            severity: 'error'
          });
        } else {
          setCopySnackbar({
            open: true,
            message: 'Copy failed. Please manually select and copy the text from the AI Prompt column using the "Select All" button.',
            severity: 'error'
          });
        }
      } catch (selectErr) {
        setCopySnackbar({
          open: true,
          message: 'Copy failed. Please manually select and copy the text from the AI Prompt column.',
          severity: 'error'
        });
      }
    }
  };

  // Robust fallback copy function
  const fallbackCopyTextToClipboard = (text: string): Promise<boolean> => {
    return new Promise((resolve) => {
      // Create a temporary textarea element
      const textArea = document.createElement('textarea');
      textArea.value = text;
      
      // Make it invisible but still functional
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      textArea.style.opacity = '0';
      textArea.style.pointerEvents = 'none';
      textArea.setAttribute('readonly', '');
      
      document.body.appendChild(textArea);
      
      try {
        // Focus and select the text
        textArea.focus();
        textArea.select();
        textArea.setSelectionRange(0, textArea.value.length);
        
        // Attempt to copy using execCommand
        const successful = document.execCommand('copy');
        document.body.removeChild(textArea);
        resolve(successful);
      } catch (err) {
        document.body.removeChild(textArea);
        resolve(false);
      }
    });
  };

  const getSelectedMethodology = () => {
    return methodologies.find(m => m.id === tempTerminologySettings.methodology);
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'High': return 'error';
      case 'Medium': return 'warning';
      case 'Low': return 'info';
      default: return 'default';
    }
  };

  // Render Data Governance Dashboard section
  const renderDataGovernanceSection = () => (
    <MuiBox>
      {/* Section Header */}
      <MuiBox sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
        <SecurityIcon sx={{ color: 'primary.main', fontSize: 32 }} />
        <MuiBox sx={{ flex: 1 }}>
          <MuiTypography variant="h4" sx={{ fontWeight: 600 }}>
            Data Governance Dashboard
          </MuiTypography>
          <MuiTypography variant="body1" color="text.secondary">
            Monitor data quality, compliance, and governance metrics across the platform
          </MuiTypography>
        </MuiBox>
        <MuiIconButton onClick={handleRefreshGovernance} sx={{ color: 'primary.main' }}>
          <RefreshIcon />
        </MuiIconButton>
      </MuiBox>

      {/* Section Content */}
      <MuiFade in timeout={300}>
        <MuiBox sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Overview Cards */}
          <MuiGrid container spacing={3}>
            <MuiGrid item xs={12} sm={6} md={3}>
              <MuiCard elevation={2}>
                <MuiCardContent sx={{ textAlign: 'center' }}>
                  <AssessmentIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                  <MuiTypography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
                    {governanceData.complianceScore}%
                  </MuiTypography>
                  <MuiTypography variant="body2" color="text.secondary">
                    Compliance Score
                  </MuiTypography>
                </MuiCardContent>
              </MuiCard>
            </MuiGrid>
            <MuiGrid item xs={12} sm={6} md={3}>
              <MuiCard elevation={2}>
                <MuiCardContent sx={{ textAlign: 'center' }}>
                  <CloudDoneIcon sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
                  <MuiTypography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
                    {governanceData.dataQualityScore}%
                  </MuiTypography>
                  <MuiTypography variant="body2" color="text.secondary">
                    Data Quality
                  </MuiTypography>
                </MuiCardContent>
              </MuiCard>
            </MuiGrid>
            <MuiGrid item xs={12} sm={6} md={3}>
              <MuiCard elevation={2}>
                <MuiCardContent sx={{ textAlign: 'center' }}>
                  <StorageIcon sx={{ fontSize: 40, color: 'info.main', mb: 1 }} />
                  <MuiTypography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
                    {governanceData.dataAssets}
                  </MuiTypography>
                  <MuiTypography variant="body2" color="text.secondary">
                    Data Assets
                  </MuiTypography>
                </MuiCardContent>
              </MuiCard>
            </MuiGrid>
            <MuiGrid item xs={12} sm={6} md={3}>
              <MuiCard elevation={2}>
                <MuiCardContent sx={{ textAlign: 'center' }}>
                  <CheckCircleIcon sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
                  <MuiTypography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
                    {governanceData.governedFields}
                  </MuiTypography>
                  <MuiTypography variant="body2" color="text.secondary">
                    Governed Fields
                  </MuiTypography>
                </MuiCardContent>
              </MuiCard>
            </MuiGrid>
          </MuiGrid>

          {/* Audit Status */}
          <MuiCard elevation={1}>
            <MuiCardHeader
              title="Audit Status"
              subheader={`Last audit: ${governanceData.lastAudit}`}
            />
            <MuiCardContent>
              <MuiBox sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <CheckCircleIcon sx={{ color: 'success.main' }} />
                <MuiTypography variant="body1">
                  Overall Status: 
                  <MuiChip 
                    label={governanceData.auditStatus.toUpperCase()} 
                    color="success" 
                    size="small" 
                    sx={{ ml: 1 }} 
                  />
                </MuiTypography>
              </MuiBox>
              
              <MuiBox sx={{ display: 'flex', gap: 4, mb: 2 }}>
                <MuiBox>
                  <MuiTypography variant="body2" color="text.secondary">
                    Active Violations
                  </MuiTypography>
                  <MuiTypography variant="h6" color="warning.main">
                    {governanceData.activeViolations}
                  </MuiTypography>
                </MuiBox>
                <MuiBox>
                  <MuiTypography variant="body2" color="text.secondary">
                    Resolved This Month
                  </MuiTypography>
                  <MuiTypography variant="h6" color="success.main">
                    {governanceData.resolvedViolations}
                  </MuiTypography>
                </MuiBox>
              </MuiBox>

              <MuiLinearProgress 
                variant="determinate" 
                value={governanceData.complianceScore} 
                sx={{ height: 8, borderRadius: 4 }}
              />
            </MuiCardContent>
          </MuiCard>

          {/* Enhanced Compliance Violations Table with AI Prompts */}
          <MuiCard elevation={1}>
            <MuiCardHeader
              title={
                <MuiBox sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <MuiTypography variant="h6">
                    Compliance Violations
                  </MuiTypography>
                  <MuiChip 
                    icon={<SmartToyIcon />} 
                    label="AI Resolution Available" 
                    color="primary" 
                    variant="outlined" 
                    size="small" 
                  />
                </MuiBox>
              }
              subheader="Monitor violations and get AI-powered resolution guidance"
            />
            <MuiCardContent>
              <MuiTableContainer component={MuiPaper} sx={{ maxHeight: 600, overflow: 'auto' }}>
                <MuiTable stickyHeader>
                  <MuiTableHead>
                    <MuiTableRow>
                      <MuiTableCell sx={{ minWidth: 140 }}>Violation Type</MuiTableCell>
                      <MuiTableCell sx={{ minWidth: 80 }}>Severity</MuiTableCell>
                      <MuiTableCell sx={{ minWidth: 140 }}>Entity</MuiTableCell>
                      <MuiTableCell sx={{ minWidth: 90 }}>Date</MuiTableCell>
                      <MuiTableCell sx={{ minWidth: 80 }}>Status</MuiTableCell>
                      <MuiTableCell align="center" sx={{ minWidth: 80 }}>Copy</MuiTableCell>
                      <MuiTableCell sx={{ minWidth: 400, maxWidth: 600 }}>AI Prompt</MuiTableCell>
                    </MuiTableRow>
                  </MuiTableHead>
                  <MuiTableBody>
                    {violations.map((violation) => (
                      <MuiTableRow key={violation.id}>
                        <MuiTableCell>
                          <MuiBox>
                            <MuiTypography variant="body2" sx={{ fontWeight: 500 }}>
                              {violation.type}
                            </MuiTypography>
                            <MuiTypography variant="caption" color="text.secondary">
                              {violation.description}
                            </MuiTypography>
                          </MuiBox>
                        </MuiTableCell>
                        <MuiTableCell>
                          <MuiChip 
                            label={violation.severity} 
                            color={getSeverityColor(violation.severity) as any} 
                            size="small" 
                          />
                        </MuiTableCell>
                        <MuiTableCell>{violation.entity}</MuiTableCell>
                        <MuiTableCell>{violation.date}</MuiTableCell>
                        <MuiTableCell>
                          <MuiChip 
                            label={violation.status} 
                            color={violation.status === 'Resolved' ? 'success' : 'warning'} 
                            size="small" 
                          />
                        </MuiTableCell>
                        <MuiTableCell align="center">
                          <MuiTooltip title="Copy AI prompt to clipboard">
                            <MuiIconButton
                              onClick={() => handleCopyAIPrompt(violation)}
                              color="primary"
                              size="small"
                            >
                              <ContentCopyIcon fontSize="small" />
                            </MuiIconButton>
                          </MuiTooltip>
                        </MuiTableCell>
                        <MuiTableCell>
                          <MuiBox sx={{ width: '100%' }}>
                            <MuiTextField
                              multiline
                              value={violation.aiPrompt}
                              variant="outlined"
                              size="small"
                              fullWidth
                              InputProps={{
                                readOnly: true,
                                style: {
                                  fontSize: '12px',
                                  fontFamily: 'monospace',
                                  lineHeight: 1.4,
                                  resize: 'vertical',
                                  minHeight: '120px',
                                  maxHeight: '300px',
                                  overflow: 'auto'
                                }
                              }}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  '& fieldset': {
                                    borderColor: 'divider',
                                  },
                                  '&:hover fieldset': {
                                    borderColor: 'primary.main',
                                  },
                                  '&.Mui-focused fieldset': {
                                    borderColor: 'primary.main',
                                  },
                                },
                                '& .MuiInputBase-inputMultiline': {
                                  resize: 'vertical !important',
                                  minHeight: '120px !important',
                                  maxHeight: '300px !important',
                                  overflow: 'auto !important'
                                }
                              }}
                            />
                            <MuiBox sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                              <MuiTypography variant="caption" color="text.secondary">
                                Drag bottom-right corner to resize
                              </MuiTypography>
                              <MuiTooltip title="Select all text for easy copying">
                                <MuiButton
                                  size="small"
                                  variant="text"
                                  onClick={(e) => {
                                    const textarea = e.currentTarget.parentElement?.parentElement?.querySelector('textarea');
                                    if (textarea) {
                                      textarea.focus();
                                      textarea.select();
                                      textarea.setSelectionRange(0, textarea.value.length);
                                    }
                                  }}
                                  sx={{ fontSize: '10px', minWidth: 'auto', px: 1 }}
                                >
                                  Select All
                                </MuiButton>
                              </MuiTooltip>
                            </MuiBox>
                          </MuiBox>
                        </MuiTableCell>
                      </MuiTableRow>
                    ))}
                  </MuiTableBody>
                </MuiTable>
              </MuiTableContainer>
              
              {/* AI Prompt Usage Instructions */}
              <MuiAlert severity="info" sx={{ mt: 3 }}>
                <MuiTypography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                  🤖 AI-Powered Resolution Guide
                </MuiTypography>
                <MuiTypography variant="body2">
                  <strong>Option 1:</strong> Click the copy icon to copy the prompt to clipboard, then paste into Claude.<br />
                  <strong>Option 2:</strong> Use the "Select All" button in the AI Prompt column, then manually copy (Ctrl+C) and paste into Claude.<br />
                  <strong>Tip:</strong> Drag the bottom-right corner of any prompt box to expand for easier reading.
                </MuiTypography>
              </MuiAlert>
            </MuiCardContent>
          </MuiCard>
        </MuiBox>
      </MuiFade>
    </MuiBox>
  );

  // Render Terminology Governance section
  const renderTerminologyGovernanceSection = () => (
    <MuiBox>
      {/* Section Header */}
      <MuiBox sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
        <TerminologyIcon sx={{ color: 'primary.main', fontSize: 32 }} />
        <MuiBox>
          <MuiTypography variant="h4" sx={{ fontWeight: 600 }}>
            Naming Guidelines & Governance
          </MuiTypography>
          <MuiTypography variant="body1" color="text.secondary">
            Configure terminology standards and naming conventions for consistent platform usage
          </MuiTypography>
        </MuiBox>
      </MuiBox>

      {/* Section Content */}
      <MuiFade in timeout={300}>
        <MuiBox sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Methodology Selection */}
          <MuiCard elevation={1}>
            <MuiCardHeader
              title="Methodology Framework"
              subheader="Choose your preferred agile/project management methodology"
            />
            <MuiCardContent>
              <MuiFormControl fullWidth>
                <MuiInputLabel>Methodology</MuiInputLabel>
                <MuiSelect
                  value={tempTerminologySettings.methodology}
                  label="Methodology"
                  onChange={handleMethodologyChange}
                >
                  {methodologies.map((methodology) => (
                    <MuiMenuItem key={methodology.id} value={methodology.id}>
                      <MuiBox>
                        <MuiTypography variant="body1">{methodology.name}</MuiTypography>
                        <MuiTypography variant="caption" color="text.secondary">
                          {methodology.description}
                        </MuiTypography>
                      </MuiBox>
                    </MuiMenuItem>
                  ))}
                </MuiSelect>
              </MuiFormControl>
              {getSelectedMethodology() && (
                <MuiAlert severity="info" sx={{ mt: 2 }}>
                  <MuiTypography variant="body2">
                    {getSelectedMethodology()?.description}
                  </MuiTypography>
                </MuiAlert>
              )}
            </MuiCardContent>
          </MuiCard>

          {/* Terminology Configuration */}
          <MuiCard elevation={1}>
            <MuiCardHeader
              title="Terminology Configuration"
              subheader="Customize terminology to match your organization's language"
              action={
                terminologyHasChanges && (
                  <MuiButton
                    variant="contained"
                    startIcon={<SaveIcon />}
                    onClick={handleSaveTerminology}
                  >
                    Save Changes
                  </MuiButton>
                )
              }
            />
            <MuiCardContent>
              <MuiGrid container spacing={3}>
                {Object.entries(tempTerminologySettings.customTerms).map(([key, value]) => (
                  <MuiGrid item xs={12} sm={6} md={4} key={key}>
                    <MuiTextField
                      fullWidth
                      label={key.split('-').map(word => 
                        word.charAt(0).toUpperCase() + word.slice(1)
                      ).join(' ')}
                      value={value}
                      onChange={(e) => handleTermChange(key, e.target.value)}
                      variant="outlined"
                      InputProps={{
                        endAdornment: (
                          <MuiTooltip title={termDefinitions[key as keyof typeof termDefinitions]}>
                            <MuiIconButton edge="end" size="small">
                              <HelpIcon fontSize="small" />
                            </MuiIconButton>
                          </MuiTooltip>
                        )
                      }}
                    />
                  </MuiGrid>
                ))}
              </MuiGrid>
              
              {terminologyHasChanges && (
                <MuiAlert severity="warning" sx={{ mt: 3 }}>
                  <MuiTypography variant="body2">
                    You have unsaved changes to your terminology settings. 
                    Save these changes to apply them across the platform.
                  </MuiTypography>
                </MuiAlert>
              )}
            </MuiCardContent>
          </MuiCard>

          {/* Best Practices Guide */}
          <MuiCard elevation={1}>
            <MuiCardHeader
              title="Naming Best Practices"
              subheader="Guidelines for creating consistent and meaningful names"
            />
            <MuiCardContent>
              <MuiList>
                <MuiListItem>
                  <MuiListItemIcon>
                    <CheckCircleIcon color="success" />
                  </MuiListItemIcon>
                  <MuiListItemText
                    primary="Use clear, descriptive names"
                    secondary="Names should clearly communicate purpose and functionality"
                  />
                </MuiListItem>
                <MuiListItem>
                  <MuiListItemIcon>
                    <CheckCircleIcon color="success" />
                  </MuiListItemIcon>
                  <MuiListItemText
                    primary="Avoid reserved words"
                    secondary="Don't use system reserved words like 'System', 'Admin', 'Default'"
                  />
                </MuiListItem>
                <MuiListItem>
                  <MuiListItemIcon>
                    <CheckCircleIcon color="success" />
                  </MuiListItemIcon>
                  <MuiListItemText
                    primary="Maintain consistency"
                    secondary="Use consistent terminology across products and features"
                  />
                </MuiListItem>
                <MuiListItem>
                  <MuiListItemIcon>
                    <WarningIcon color="warning" />
                  </MuiListItemIcon>
                  <MuiListItemText
                    primary="Prevent duplicates"
                    secondary="Ensure names are unique within their scope to avoid conflicts"
                  />
                </MuiListItem>
              </MuiList>
            </MuiCardContent>
          </MuiCard>
        </MuiBox>
      </MuiFade>
    </MuiBox>
  );

  // Render navigation column
  const renderAdminNavigation = () => (
    <ColumnNavigation
      items={navigationItems}
      selectedId={selectedSection}
      onItemSelect={handleNavigationClick}
      initialWidth={280}
      sx={{
        position: 'sticky',
        top: 0,
        overflowY: 'auto'
      }}
    />
  );

  // Main render
  return (
    <MuiBox sx={{ 
      display: 'flex', 
      height: '100vh',
      bgcolor: '#F1F5FE' // Background color for consistency
    }}>
      {/* Main Content Column */}
      <MuiBox sx={{ 
        flex: 1, 
        overflow: 'auto',
        p: 3,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        {/* Admin Panel Updated Message */}
        <MuiCard elevation={2} sx={{ maxWidth: 600, width: '100%' }}>
          <MuiCardHeader
            title="Admin Panel Updated"
            subheader="Administrative sections have been reorganized"
            titleTypographyProps={{ variant: 'h4', sx: { fontWeight: 600 } }}
          />
          <MuiCardContent>
            <MuiBox sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <MuiTypography variant="body1">
                The Admin panel sections have been moved to more appropriate locations for better user experience:
              </MuiTypography>
              
              <MuiBox sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <MuiBox sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, bgcolor: 'primary.light', borderRadius: 1, opacity: 0.1 }}>
                  <SecurityIcon sx={{ color: 'primary.main' }} />
                  <MuiBox>
                    <MuiTypography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      Data Governance Dashboard
                    </MuiTypography>
                    <MuiTypography variant="body2" color="text.secondary">
                      Now available in Profile → Privacy & Data Settings
                    </MuiTypography>
                  </MuiBox>
                </MuiBox>
                
                <MuiBox sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, bgcolor: 'primary.light', borderRadius: 1, opacity: 0.1 }}>
                  <TerminologyIcon sx={{ color: 'primary.main' }} />
                  <MuiBox>
                    <MuiTypography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      Naming Guidelines & Governance
                    </MuiTypography>
                    <MuiTypography variant="body2" color="text.secondary">
                      Now available in Profile → Privacy & Data Settings
                    </MuiTypography>
                  </MuiBox>
                </MuiBox>
              </MuiBox>
              
              <MuiBox sx={{ display: 'flex', gap: 2, mt: 2 }}>
                <MuiButton 
                  variant="contained" 
                  onClick={() => onViewChange('profile')}
                  startIcon={<PersonIcon />}
                >
                  Go to Profile
                </MuiButton>
                <MuiButton 
                  variant="outlined" 
                  onClick={() => onViewChange('projects')}
                >
                  Back to Products
                </MuiButton>
              </MuiBox>
            </MuiBox>
          </MuiCardContent>
        </MuiCard>
      </MuiBox>

      {/* Copy Success/Error Snackbar */}
      <MuiSnackbar
        open={copySnackbar.open}
        autoHideDuration={6000}
        onClose={() => setCopySnackbar({ open: false, message: '', severity: 'success' })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <MuiAlert
          onClose={() => setCopySnackbar({ open: false, message: '', severity: 'success' })}
          severity={copySnackbar.severity}
          variant="filled"
          sx={{ width: '100%', maxWidth: 400 }}
        >
          {copySnackbar.message}
        </MuiAlert>
      </MuiSnackbar>
    </MuiBox>
  );
};

export default Admin;